import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle2 } from 'lucide-react';

const packages = [
  {
    value: 'starter',
    label: 'Starter Plan',
    price: 'Free',
    features: ['5 screenings per day', 'Basic compliance', 'Standard data sources', 'Email support']
  },
  {
    value: 'professional',
    label: 'Professional Plan',
    price: '$49/month',
    features: ['500 screenings/month', 'All data sources', 'Advanced AI analysis', 'Bulk screening', 'Priority support']
  },
  {
    value: 'enterprise',
    label: 'Enterprise Plan',
    price: '$149/month',
    features: ['Unlimited screenings', 'All data sources incl. UANI, IEI, GUR', 'API access', 'Dedicated manager', 'Premium support']
  }
];
const PAID_PACKAGES = ['professional', 'enterprise'];

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storedPackage = localStorage.getItem('selectedPackage') || searchParams.get('package');
  const preSelectedPackage = packages.some(p => p.value === storedPackage) ? storedPackage : null;
  const [formData, setFormData] = useState({
    displayName: '',
    companyName: '',
    phone: '',
    jobTitle: '',
    packageType: preSelectedPackage || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await base44.auth.me();
        setUser(userData);
        
        // Pre-fill if data exists
        if (userData.display_name) {
          setFormData(prev => ({ ...prev, displayName: userData.display_name }));
        }
        if (userData.company_name) {
          setFormData(prev => ({ ...prev, companyName: userData.company_name }));
        }
        if (userData.phone) {
          setFormData(prev => ({ ...prev, phone: userData.phone }));
        }
        if (userData.job_title) {
          setFormData(prev => ({ ...prev, jobTitle: userData.job_title }));
        }
        
        // If user already completed onboarding (has company_name), redirect to dashboard
        if (userData.company_name && userData.tenant_id) {
          navigate(createPageUrl('Dashboard'));
        }
      } catch (err) {
        // Not authenticated, redirect to landing
        navigate(createPageUrl('LandingPage'));
      }
    };
    loadUser();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    if (!formData.packageType) {
      setError('Please select a package');
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if user is being invited to an existing tenant using backend function to bypass RLS
      const inviteCheckResult = await base44.functions.invoke('checkUserInvitation', { 
        email: user.email 
      });
      
      let tenantId;
      let complianceRole;
      let packageType = formData.packageType;

      let isNewTenant = false;
      if (inviteCheckResult.data?.invitation) {
        // User was invited to existing tenant
        const invite = inviteCheckResult.data.invitation;
        tenantId = invite.tenant_id;
        complianceRole = invite.role; // Use role assigned during invitation
        
        // Get inviter's package type - invited users inherit the admin's package
        const inviterData = inviteCheckResult.data.inviter;
        if (inviterData?.package_type) {
          packageType = inviterData.package_type;
        }
        
        console.log(`User ${user.email} joining tenant ${tenantId} with role ${complianceRole} and package ${packageType}`);
      } else {
        // New tenant creator - generate new tenant_id and make them Admin_user (tenant admin)
        isNewTenant = true;
        const { v4: uuidv4 } = await import('uuid');
        tenantId = uuidv4();
        complianceRole = 'Admin_user'; // New signups become tenant admins
        // packageType stays as selected by user
        
        console.log(`Creating new tenant ${tenantId} for user ${user.email} with role ${complianceRole}`);
      }

      // Update user with all data including tenant_id and package
      await base44.auth.updateMe({
        display_name: formData.displayName.trim(),
        company_name: formData.companyName.trim(),
        phone: formData.phone.trim() || undefined,
        job_title: formData.jobTitle.trim() || undefined,
        // Paid plans start as Starter; the Stripe webhook upgrades after payment
        package_type: isNewTenant && PAID_PACKAGES.includes(packageType) ? 'starter' : packageType,
        tenant_id: tenantId,
        compliance_role: complianceRole
      });

      // Clear stored package after successful onboarding
      localStorage.removeItem('selectedPackage');
      
      // Wait for update to propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // If new tenant creator selected Professional, redirect to Stripe checkout for payment
      if (isNewTenant && PAID_PACKAGES.includes(packageType)) {
        try {
          const checkoutResponse = await base44.functions.invoke('createStripeCheckout', {
            package_type: packageType,
            billing_cycle: 'monthly'
          });
          if (checkoutResponse.data?.url) {
            window.location.href = checkoutResponse.data.url;
            return;
          }
        } catch (err) {
          console.error('Stripe checkout error:', err);
          setError('Profile saved, but checkout failed to start. Please complete payment from the dashboard.');
          setIsSubmitting(false);
          return;
        }
      }

      // Redirect to dashboard (starter/free plans or invited users)
      window.location.href = createPageUrl('Dashboard');
    } catch (err) {
      setError(err.message || 'Failed to save information. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6960b95861a068b7e8045644/963ec1955_WC360noBgColor.png" 
            alt="World Check 360" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-slate-900">Welcome to World Check 360</h1>
          <p className="text-slate-600 mt-2 text-lg">Your compliance journey starts here</p>
          {preSelectedPackage && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-full">
              <CheckCircle2 className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-900">
                {packages.find(p => p.value === preSelectedPackage)?.label} selected
              </span>
            </div>
          )}
        </div>

        <Card className="border-0 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
            <CardDescription className="text-base">
              Just a few details to get you started with advanced compliance screening
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email (read-only) */}
              <div>
                <Label htmlFor="email" className="text-slate-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={user.email}
                  className="mt-1.5 h-11 bg-slate-50"
                  disabled
                />
              </div>

              {/* Display Name - Required */}
              <div>
                <Label htmlFor="displayName" className="text-slate-700">
                  Display Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="John Doe"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  className="mt-1.5 h-11"
                  required
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              {/* Company Name - Required */}
              <div>
                <Label htmlFor="companyName" className="text-slate-700">
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="mt-1.5 h-11"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Phone Number - Optional */}
              <div>
                <Label htmlFor="phone" className="text-slate-700">
                  Phone Number <span className="text-slate-500 text-sm">(optional)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1.5 h-11"
                  disabled={isSubmitting}
                />
              </div>

              {/* Job Title - Optional */}
              <div>
                <Label htmlFor="jobTitle" className="text-slate-700">
                  Job Title <span className="text-slate-500 text-sm">(optional)</span>
                </Label>
                <Input
                  id="jobTitle"
                  type="text"
                  placeholder="Compliance Officer"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  className="mt-1.5 h-11"
                  disabled={isSubmitting}
                />
              </div>

              {/* Package Selection - Only show if not pre-selected */}
              {!preSelectedPackage ? (
                <div>
                  <Label htmlFor="package" className="text-slate-700">
                    Select Package <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={formData.packageType} 
                    onValueChange={(value) => setFormData({ ...formData, packageType: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-11 mt-1.5">
                      <SelectValue placeholder="Choose a package" />
                    </SelectTrigger>
                    <SelectContent>
                      {packages.map((pkg) => (
                        <SelectItem key={pkg.value} value={pkg.value}>
                          <div className="flex flex-col py-1">
                            <span className="font-medium">{pkg.label}</span>
                            <span className="text-xs text-slate-500">{pkg.price}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                /* Show selected package details as read-only */
                <div>
                  <Label className="text-slate-700">Your Selected Package</Label>
                  <div className="mt-1.5 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-indigo-900 text-base">
                          {packages.find(p => p.value === formData.packageType)?.label}
                        </h4>
                        <p className="text-sm text-indigo-700 font-medium">
                          {packages.find(p => p.value === formData.packageType)?.price}
                        </p>
                      </div>
                      <CheckCircle2 className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="space-y-2">
                      {packages.find(p => p.value === formData.packageType)?.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg shadow-indigo-200"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Setting up your workspace...
                  </>
                ) : (
                  <>
                    Start Screening Now →
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                By continuing, you agree to our <span className="text-indigo-600 hover:underline cursor-pointer">Terms of Service</span> and <span className="text-indigo-600 hover:underline cursor-pointer">Privacy Policy</span>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}